<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue';
import hotkeys from 'hotkeys-js';
import { uniqueId } from 'lodash-es';
import type { BaseModalProps } from '@/modules/common/types/ui.ts';
import { useFocusTrap } from '@/modules/ui/composables/use-focus-trap.ts';
import { useModalStack } from '@/modules/ui/composables/use-modal-stack.ts';
import { provideInModal } from '@/providers/in-modal.ts';
import { provideModalHotkeysScope } from '@/providers/modal-hotkeys-scope.ts';

const isOpen = defineModel<boolean>({ required: true });

const isClosing = ref(false);

const {
  width,
  popup,
  height,
  center,
  centered,
  bare,
  priority,
  noBlur,
  float,
  mobileFullHeight = false,
  clipContent = false,
  scope: scopeProp,
  suspended = false,
} = defineProps<
  BaseModalProps & {
    popup?: boolean;
    priority?: boolean;
    centered?: boolean;
    noBlur?: boolean;
    bare?: boolean;
    noSelect?: boolean;
    suspended?: boolean;
    float?: { top?: string; bottom?: string; left?: string; right?: string };
    scope?: string;
  }
>();

const emit = defineEmits<{
  show: [];
  hide: [];
  close: [];
  submit: [];
}>();

const instance = getCurrentInstance();

let previousScope = 'all';
let shown = false;

const hotkeysScope = ref('');

provideInModal(true);
provideModalHotkeysScope(hotkeysScope);

const isCentered = computed(() => centered ?? center);
const widthComputed = computed(() => width || (popup ? 'xxs' : 's'));
const contentClass = computed(() => [`_width-${widthComputed.value}`, { '_with-height': height }]);

const { isTop, isBottom } = useModalStack({
  isActive: () => isOpen.value && !bare && !suspended,
  priority: () => !!priority,
});

const modalRef = ref<HTMLElement>();
const { enable: enableFocusTrap, disable: disableFocusTrap } = useFocusTrap(modalRef);

watch(isOpen, (isOpen) => (isOpen ? onShow() : onHide()));
watch(
  () => suspended,
  (value) => {
    if (!isOpen.value || !shown) {
      return;
    }

    if (value) {
      pauseInteraction();
    } else {
      resumeInteraction();
    }
  }
);
onMounted(() => {
  if (isOpen.value) {
    onShow();
  }
});

onUnmounted(onHide);

function bindHotkeys() {
  previousScope = hotkeys.getScope();
  hotkeysScope.value = scopeProp ?? uniqueId('modal-');
  hotkeys.setScope(hotkeysScope.value);
  hotkeys('escape', hotkeysScope.value, close);

  if (instance?.vnode.props?.onSubmit) {
    hotkeys('ctrl+s, command+s', hotkeysScope.value, onSubmit);
  }
}

function unbindHotkeys() {
  const restoreScope = previousScope;
  const leavingScope = hotkeysScope.value;
  hotkeys.unbind('escape', leavingScope, close);
  hotkeys.unbind('ctrl+s, command+s', leavingScope, onSubmit);
  hotkeysScope.value = '';

  queueMicrotask(() => {
    if (hotkeys.getScope() === leavingScope) {
      hotkeys.setScope(restoreScope);
    }
  });
}

function pauseInteraction() {
  disableFocusTrap({ restoreFocus: false });
  unbindHotkeys();
}

function resumeInteraction() {
  hotkeys.setScope('all');
  bindHotkeys();
  enableFocusTrap();
}

function onShow() {
  if (shown) {
    return;
  }

  shown = true;
  bindHotkeys();
  enableFocusTrap();
  emit('show');

  if (suspended) {
    pauseInteraction();
  }
}

function onHide() {
  if (!shown) {
    return;
  }

  shown = false;
  unbindHotkeys();
  disableFocusTrap();
  emit('hide');
}

function close(event?: KeyboardEvent) {
  event?.preventDefault();
  event?.stopImmediatePropagation();
  isOpen.value = false;
  emit('close');
  return false;
}

function onSubmit(event: KeyboardEvent) {
  event.preventDefault();
  (document.activeElement as HTMLElement | null)?.blur();
  emit('submit');
}

function onBackdropMouseUp() {
  if (isClosing.value) {
    close();
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="backdrop">
      <div
        v-if="isOpen && !bare && !suspended && isBottom"
        class="backdrop"
        :class="{ _priority: priority, _blur: !noBlur }"
        aria-hidden="true"
      />
    </Transition>

    <Transition name="fade">
      <div
        v-if="isOpen"
        v-show="!suspended"
        ref="modalRef"
        class="ui-modal"
        :class="{
          '_priority': priority,
          '_centered': isCentered,
          '_bare': bare,
          '_no-select': noSelect,
          '_mobile-full-height': mobileFullHeight,
        }"
        @mousedown.self="isClosing = true"
        @mouseup.self="onBackdropMouseUp"
        @mouseup="isClosing = false"
      >
        <div
          class="content"
          :class="[contentClass, { '_float': !!float, '_clip-content': clipContent }]"
          :style="{ height, minHeight, minWidth, maxWidth, ...float }"
        >
          <slot>
            <div
              class="sheet"
              :class="{ _centered: popup }"
            >
              <header
                v-if="$slots.header || title"
                class="header"
              >
                <slot name="header">
                  <h2
                    v-if="title"
                    class="title"
                  >
                    {{ $t(title) }}
                  </h2>
                </slot>
              </header>

              <div
                v-if="$slots.body"
                class="body"
                :class="{ _popup: popup }"
              >
                <slot name="body" />
              </div>

              <footer
                v-if="$slots.footer"
                class="footer"
              >
                <slot name="footer" />
              </footer>
            </div>
          </slot>

          <slot
            name="overlay"
            :target="modalRef"
          />

          <div
            v-if="!bare"
            class="shade"
            :class="{ _visible: !isTop }"
            aria-hidden="true"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
.ui-modal {
  /* One source for the gap around the modal: the padding and the height cap both
     read it, so they cannot drift apart. */
  --modal-edge: 24px;

  /* Roomy screens keep the extra breathing room under the modal. */
  --modal-cap: calc(100dvh - 80px);

  /* dvh, not vh: on a phone vh is the toolbar-hidden height, so the modal would
     reach past what the user can actually see. */
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--z-index-modal);
  width: 100%;
  height: 100dvh;
  padding: var(--modal-edge);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  &._bare {
    padding: 0;
    overflow: visible;
  }

  &._no-select {
    user-select: none;

    input,
    textarea,
    [contenteditable] {
      user-select: text;
    }
  }

  @include respond-down(m) {
    --modal-edge: 16px;
  }

  @include respond-down(s) {
    --modal-edge: 8px;
  }

  /* A phone in landscape is wide but short, so no width step ever catches it.
     Give the edge room back and let the modal use the whole visible height. */
  @include respond-down(s, height) {
    --modal-edge: 8px;
    --modal-cap: calc(100dvh - 2 * var(--modal-edge));
  }

  &._priority {
    z-index: var(--z-index-priority-modal);
  }

  &._mobile-full-height {
    @include respond-down(s) {
      --modal-cap: calc(100dvh - 2 * var(--modal-edge));
    }
  }

  &._centered {
    display: flex;

    .content {
      width: 100%;
      margin: auto;
    }
  }
}

.backdrop {
  position: fixed;
  top: 0;
  left: 0;
  z-index: var(--z-index-modal);
  width: 100%;
  height: 100%;
  pointer-events: none;
  background-color: var(--color-backdrop);

  &._priority {
    z-index: var(--z-index-priority-modal);
  }

  &._blur {
    backdrop-filter: blur(5px);
  }

  &-enter-active,
  &-leave-active {
    transition: opacity var(--transition);
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;
  }
}

.content {
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  transition:
    height var(--transition-medium),
    max-width var(--transition-medium);

  &._clip-content {
    border-radius: 8px;
    overflow: hidden;
  }

  &._float {
    position: absolute;
    margin: 0;
  }

  &._with-height {
    max-height: var(--modal-cap);
    overflow: hidden;

    .sheet,
    .body {
      height: 100%;
      min-height: 0;
    }
  }

  &._width-xxs {
    max-width: 300px;
  }

  &._width-xs {
    max-width: 400px;
  }

  &._width-s {
    max-width: 500px;
  }

  &._width-m {
    max-width: 720px;
  }

  &._width-l {
    max-width: 900px;
  }

  &._width-xl {
    max-width: 1140px;
  }
}

.shade {
  position: absolute;
  z-index: var(--z-index-modal-tooltip);
  inset: 0;
  border-radius: 8px;
  opacity: 0;
  background-color: var(--color-backdrop-light);
  transition: opacity var(--transition);
  pointer-events: none;

  &._visible {
    opacity: 1;
  }
}

.sheet {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: inherit;
  width: 100%;
  border-radius: 8px;
  background-color: var(--color-white);

  ._bare & {
    box-shadow: var(--shadow);
  }

  &._centered {
    .title,
    .body {
      text-align: center;
    }

    .footer {
      justify-content: center;
    }
  }
}

.header {
  flex-shrink: 0;
  padding: 16px;
}

.body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 0 16px;

  &:first-child {
    padding-top: 16px;
  }

  &:last-child {
    padding-bottom: 16px;
  }

  &._popup {
    padding: 16px;
    color: var(--color-gray-600);
    word-break: break-word;
  }
}

.footer {
  display: flex;
  flex-shrink: 0;
  padding: 16px;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.fade {
  &-enter-active,
  &-leave-active {
    transition: opacity var(--transition);

    .content {
      transition: transform var(--transition);
    }
  }

  &-enter-from,
  &-leave-to {
    opacity: 0;

    .content {
      transform: translateY(-32px);
    }
  }
}
</style>
